import { useEffect, useState } from 'react';
import { cn } from '../utils.js';

export interface SectionLink {
  id: string;
  label: string;
}

/** The page's side menu — a real column in the layout (see the grid in
 * `RikuzPage`), sticky under the header, that jumps to and tracks the page's
 * own section anchors. Hidden below `xl`: on a narrow screen there is no room
 * for a column beside the content. */
export function SectionNav({ sections }: { sections: SectionLink[] }) {
  const [active, setActive] = useState<string | null>(sections[0]?.id ?? null);

  useEffect(() => {
    const els = sections.map((s) => document.getElementById(s.id)).filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
        setActive(top.target.id);
      },
      // A section "counts" once it's within the top 30% of the viewport, and
      // until it's scrolled past the top — keeps the highlight matching what
      // the reader is actually looking at, not just barely on-screen.
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    for (const el of els) observer.observe(el);
    return () => observer.disconnect();
  }, [sections]);

  if (sections.length === 0) return null;

  return (
    <nav
      aria-label="ניווט לפי אזור"
      // `top` clears the sticky header; the max-height + scroll keeps a long
      // menu usable on a short window instead of running off the bottom.
      className="sticky top-24 hidden max-h-[calc(100vh-8rem)] self-start overflow-y-auto border-e border-border pe-4 xl:block"
    >
      <div className="flex flex-col gap-0.5">
        <div className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">ניווט</div>
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            aria-current={active === s.id ? 'true' : undefined}
            onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className={cn(
              'relative rounded-md px-3 py-2 text-start text-sm transition-colors',
              // The accent bar sits on the start edge — the side the menu's own
              // divider is not on, so it reads as "you are here" in RTL.
              'before:absolute before:inset-y-1.5 before:start-0 before:w-0.5 before:rounded-full before:bg-primary before:opacity-0 before:transition-opacity',
              active === s.id
                ? 'bg-muted font-semibold text-foreground before:opacity-100'
                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
    </nav>
  );
}
